/**
 * 붙은 자료 칩 — 썸네일(파일 확장자·유튜브·웹 로고) + 제목 + 메타 + ✕. 순수 UI 다.
 * 올리는 중·가져오는 중이면 썸네일 자리에만 진행 표시를 두고 메타 줄은 그대로다.
 * Figma `.AIGen/AttachmentChip`(40707:14225) — 파일 「PDF · 4.2MB」, 유튜브 「youtube.com · 12:34」,
 * 웹 「ko.wikipedia.org」.
 */
import { CircularProgress, IconButton } from '@zep-us/zus';
import { Blue500, ICON_COLOR } from '@zep-us/zus/tokens';
import { GlobeFillIcon, XSmallIcon, YoutubeColorIcon } from '@zep-us/zus-icons';
import { cn } from '@/utils/tailwind.util';
import { type AiGenerateDockAttachment, type AiGenerateDockCopy } from '../types/aiGenerateDock.types';
import { formatAiGenerateDockDuration, formatAiGenerateDockLinkHost } from '../utils/aiGenerateDockAttachmentMeta';
import { AiBuilderAttachmentExtensionIcon } from './AiBuilderAttachmentExtensionIcon';
const getFileExtension = (name: string) => name.split('.').pop() ?? '';
const resolveMeta = (attachment: AiGenerateDockAttachment) => {
  if (attachment.status === 'error') return attachment.errorMessage;
  if (attachment.kind === 'file') {
    return [getFileExtension(attachment.name).toUpperCase(), attachment.sizeLabel].filter(Boolean).join(' · ');
  }
  return [formatAiGenerateDockLinkHost(attachment.sourceType, attachment.url), attachment.durationSeconds ? formatAiGenerateDockDuration(attachment.durationSeconds) : undefined].filter(Boolean).join(' · ');
};
const Thumbnail = ({
  attachment,
  loadingLabel
}: {
  attachment: AiGenerateDockAttachment;
  loadingLabel: string;
}) => {
  if (attachment.status === 'uploading' || attachment.status === 'fetching') {
    return <CircularProgress size="sm" intent="strong" aria-label={loadingLabel} />;
  }
  if (attachment.kind === 'file') {
    return <AiBuilderAttachmentExtensionIcon extension={getFileExtension(attachment.name)} className="size-lg" />;
  }
  return attachment.sourceType === 'youtube' ? <YoutubeColorIcon size="md" aria-hidden data-sentry-element="YoutubeColorIcon" data-sentry-component="Thumbnail" data-sentry-source-file="AiGenerateDockAttachmentChip.tsx" /> : <GlobeFillIcon size="md" fill={Blue500} aria-hidden data-sentry-element="GlobeFillIcon" data-sentry-component="Thumbnail" data-sentry-source-file="AiGenerateDockAttachmentChip.tsx" />;
};
export const AiGenerateDockAttachmentChip = ({
  attachment,
  copy,
  onRemove,
  disabled
}: {
  attachment: AiGenerateDockAttachment;
  copy: AiGenerateDockCopy;
  onRemove: () => void;
  disabled?: boolean;
}) => {
  const isLoading = attachment.status === 'uploading' || attachment.status === 'fetching';
  const title = attachment.kind === 'file' ? attachment.name : attachment.title ?? attachment.url;
  const meta = resolveMeta(attachment);
  // 진행 표시의 이름 — 화면엔 메타 줄을 그대로 두고, 보조기기에만 「올리는 중…」을 알린다.
  const loadingLabel = copy.attachmentStatus[attachment.status === 'uploading' ? 'uploading' : 'fetching'];
  return <div data-testid="ai-generate-dock-attachment" data-state={attachment.status} aria-busy={isLoading || undefined} className={cn(
  // Figma .AIGen/AttachmentChip 최대 폭 250 — 넘치면 이름만 말줄임, 메타 줄·✕ 는 늘 보인다.
  'flex min-w-0 max-w-[250px] items-center gap-xs rounded-md border bg-default p-xs', attachment.status === 'error' ? 'border-destructive-assistive' : 'border-alternative')} data-sentry-component="AiGenerateDockAttachmentChip" data-sentry-source-file="AiGenerateDockAttachmentChip.tsx">
      <div className="flex size-[36px] shrink-0 items-center justify-center rounded-sm bg-neutral">
        <Thumbnail attachment={attachment} loadingLabel={loadingLabel} data-sentry-element="Thumbnail" data-sentry-source-file="AiGenerateDockAttachmentChip.tsx" />
      </div>
      <div className="flex min-w-0 flex-col gap-4xs">
        <p className="truncate text-body-sm-semibold text-default">{title}</p>
        {meta && <p className={cn('truncate text-caption-lg-medium', attachment.status === 'error' ? 'text-destructive' : 'text-assistive')}>
            {meta}
          </p>}
      </div>
      <IconButton size="2xs" className="shrink-0" variant="transparent" intent="neutral" aria-label={copy.removeAttachment(title)} data-testid="ai-generate-dock-remove-attachment" disabled={disabled} onClick={onRemove} data-sentry-element="IconButton" data-sentry-source-file="AiGenerateDockAttachmentChip.tsx">
        <XSmallIcon size="xs" fill={ICON_COLOR.neutral} data-sentry-element="XSmallIcon" data-sentry-source-file="AiGenerateDockAttachmentChip.tsx" />
      </IconButton>
    </div>;
};